import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useToast } from '../components/Toast';
import { formatUSDT, formatNumber, formatSmart } from '../utils/helpers';
import { MATRIX_LEVELS } from '../config/constants';
import Loader from '../components/Loader';
import './Team.css';

const Team = () => {
  const { account, storageContract, planContract, userDetails } = useWeb3();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [teamStats, setTeamStats] = useState({
    teamCount: 0,
    teamBusiness: '0',
    directCount: 0,
    directAutoCount: 0
  });
  const [levelStats, setLevelStats] = useState([]);
  const [claimableIncome, setClaimableIncome] = useState({
    sponsor: '0',
    level: '0',
    autoRef: '0',
    total: '0'
  });
  const [missedIncome, setMissedIncome] = useState({
    sponsor: '0',
    level: '0',
    total: '0'
  });

  // Eligibility state (loaded from plan + storage contracts)
  const [eligibility, setEligibility] = useState({
    reqL15: 100,        // directAutoBusinessRequiredL1to15 (USDT)
    reqL30: 1000,       // directAutoBusinessRequiredL16to30 (USDT)
    period: 7776000,    // eligibilityPeriod (seconds, default 90 days)
    minRefs: 1,         // eligibilityMinAutoReferrals
    minVolume: 1000,    // eligibilityMinVolume (USDT)
    startTime: 0,       // eligibilityStartTime for current user
    autoRefs: 0,        // eligibilityAutoReferrals for current user
    autoVolume: 0,      // eligibilityAutoVolume for current user
    isEligible: false,  // isEligibleForLevel
    wasEverEligible: false
  });

  // Live ticker for countdown display (refreshes every second)
  const [nowTs, setNowTs] = useState(Math.floor(Date.now() / 1000));
  useEffect(() => {
    const t = setInterval(() => setNowTs(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  const directAutoUserBusiness = parseFloat(formatUSDT(userDetails?.directAutoUserBusiness || 0));

  // Derived eligibility status
  const isL15Unlocked = eligibility.isEligible && directAutoUserBusiness >= eligibility.reqL15;
  const isL30Unlocked = eligibility.isEligible && directAutoUserBusiness >= eligibility.reqL30;
  const progressL15 = Math.min((directAutoUserBusiness / Math.max(eligibility.reqL15, 1)) * 100, 100);
  const progressL30 = Math.min((directAutoUserBusiness / Math.max(eligibility.reqL30, 1)) * 100, 100);

  // 90-day timer
  const periodEnd = eligibility.startTime > 0 ? eligibility.startTime + eligibility.period : 0;
  const secondsLeft = periodEnd > nowTs ? periodEnd - nowTs : 0;
  const periodExpired = eligibility.startTime > 0 && nowTs > periodEnd;
  const renewalMet = eligibility.autoRefs >= eligibility.minRefs && eligibility.autoVolume >= eligibility.minVolume;

  useEffect(() => {
    fetchTeamData();
    // eslint-disable-next-line
  }, [storageContract, planContract, account]);

  const fetchTeamData = async () => {
    if (!storageContract || !planContract || !account) {
      setLoading(false);
      return;
    }

    try {
      const [
        stats, allLevels, claimable, missed,
        reqL15, reqL30, period, minRefs, minVol,
        startTime, autoRefs, autoVol, isEl, wasEl
      ] = await Promise.all([
        storageContract.getUserTeamStats(account),
        storageContract.getAllLevelStats(account),
        storageContract.getClaimableIncome(account),
        storageContract.getMissedIncome(account),
        planContract.directAutoBusinessRequiredL1to15(),
        planContract.directAutoBusinessRequiredL16to30(),
        planContract.eligibilityPeriod(),
        planContract.eligibilityMinAutoReferrals(),
        planContract.eligibilityMinVolume(),
        storageContract.eligibilityStartTime(account),
        storageContract.eligibilityAutoReferrals(account),
        storageContract.eligibilityAutoVolume(account),
        storageContract.isEligibleForLevel(account),
        storageContract.wasEverEligible(account)
      ]);

      setTeamStats({
        teamCount: stats.teamCount.toString(),
        teamBusiness: formatUSDT(stats.teamBusiness),
        directCount: stats.directCount.toString(),
        directAutoCount: stats.directAutoCount.toString()
      });

      const levels = [];
      const totalLevels = Math.min(MATRIX_LEVELS, allLevels.userCounts.length);
      for (let i = 0; i < totalLevels; i++) {
        levels.push({
          level: i + 1,
          users: allLevels.userCounts[i].toString(),
          business: formatUSDT(allLevels.businesses[i])
        });
      }
      setLevelStats(levels);

      setClaimableIncome({
        sponsor: formatUSDT(claimable.sponsor),
        level: formatUSDT(claimable.level),
        autoRef: formatUSDT(claimable.autoRef),
        total: formatUSDT(claimable.total)
      });

      setMissedIncome({
        sponsor: formatUSDT(missed.sponsor),
        level: formatUSDT(missed.level),
        total: formatUSDT(missed.total)
      });

      setEligibility({
        reqL15: parseFloat(formatUSDT(reqL15)),
        reqL30: parseFloat(formatUSDT(reqL30)),
        period: Number(period.toString()),
        minRefs: Number(minRefs.toString()),
        minVolume: parseFloat(formatUSDT(minVol)),
        startTime: Number(startTime.toString()),
        autoRefs: Number(autoRefs.toString()),
        autoVolume: parseFloat(formatUSDT(autoVol)),
        isEligible: isEl,
        wasEverEligible: wasEl
      });
    } catch (error) {
      console.error('Error fetching team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  // Format seconds → "Xd Yh Zm" or "Zm Ss" for short periods
  const formatDuration = (secs) => {
    if (secs <= 0) return 'Expired';
    const days = Math.floor(secs / 86400);
    const hours = Math.floor((secs % 86400) / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (days > 0) return `${days}d ${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${s}s`;
    return `${s}s`;
  };

  if (loading) {
    return <Loader text="Loading Team" />;
  }

  return (
    <div className="team-page">
      {/* Page Header */}
      <div className="page-header animate-fade-up">
        <h1 className="page-title">
          <span className="title-icon pulse-icon">👥</span>
          My Team
        </h1>
      </div>

      {/* Team Overview */}
      <div className="team-overview animate-fade-up delay-1">
        <div className="overview-card">
          <div className="overview-icon">👥</div>
          <div className="overview-info">
            <span className="overview-value font-numbers">{formatNumber(parseInt(teamStats.teamCount), 0)}</span>
            <span className="overview-label">Total Team</span>
          </div>
        </div>
        <div className="overview-card">
          <div className="overview-icon">🎯</div>
          <div className="overview-info">
            <span className="overview-value font-numbers">{teamStats.directCount}</span>
            <span className="overview-label">Direct Referrals</span>
          </div>
        </div>
        <div className="overview-card full-width">
          <div className="overview-icon">💰</div>
          <div className="overview-info">
            <span className="overview-value font-numbers">${formatNumber(parseFloat(teamStats.teamBusiness))}</span>
            <span className="overview-label">Team Business</span>
          </div>
        </div>
      </div>

      {/* Level Income Eligibility — dual threshold */}
      <div className={`eligibility-card glass-card animate-fade-up delay-2 ${isL15Unlocked ? 'eligible' : 'not-eligible'}`}>
        <div className="eligibility-header">
          <span className="eligibility-icon">{isL15Unlocked ? '✅' : '🔒'}</span>
          <h3>Level Income Eligibility</h3>
        </div>
        <div className="eligibility-content">
          <div className="eligibility-note" style={{ marginBottom: '12px' }}>
            <span className="note-icon">💰</span>
            <span>Your Direct Auto Business: <strong>${formatNumber(directAutoUserBusiness)}</strong></span>
          </div>

          {/* L1-L15 threshold */}
          <div style={{ marginBottom: '16px', padding: '10px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <strong>{isL15Unlocked ? '🎉' : '🔒'} Levels 1 – 15 (0.025% each)</strong>
              <span style={{ color: isL15Unlocked ? 'var(--a, #39ff14)' : 'var(--t2, #888)', fontSize: '.8rem' }}>
                {isL15Unlocked ? 'UNLOCKED' : 'LOCKED'}
              </span>
            </div>
            <div className="progress-bar" style={{ height: '10px' }}>
              <div className="progress-fill" style={{ width: `${progressL15}%` }}></div>
            </div>
            <div className="progress-info" style={{ fontSize: '.75rem', marginTop: '4px' }}>
              <span className="current font-numbers">${formatNumber(directAutoUserBusiness)}</span>
              <span className="required">/ ${eligibility.reqL15} Required</span>
            </div>
          </div>

          {/* L16-L30 threshold */}
          <div style={{ marginBottom: '12px', padding: '10px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <strong>{isL30Unlocked ? '🎉' : '🔒'} Levels 16 – 30 (0.01% each)</strong>
              <span style={{ color: isL30Unlocked ? 'var(--a, #39ff14)' : 'var(--t2, #888)', fontSize: '.8rem' }}>
                {isL30Unlocked ? 'UNLOCKED' : 'LOCKED'}
              </span>
            </div>
            <div className="progress-bar" style={{ height: '10px' }}>
              <div className="progress-fill" style={{ width: `${progressL30}%` }}></div>
            </div>
            <div className="progress-info" style={{ fontSize: '.75rem', marginTop: '4px' }}>
              <span className="current font-numbers">${formatNumber(directAutoUserBusiness)}</span>
              <span className="required">/ ${eligibility.reqL30} Required</span>
            </div>
          </div>

          {/* 90-day renewal timer — only shown if user has ever been eligible */}
          {eligibility.startTime > 0 && (
            <div className={`eligibility-note ${periodExpired && !renewalMet ? 'warn' : ''}`} style={{
              padding: '12px',
              background: periodExpired
                ? (renewalMet ? 'rgba(57,255,20,0.08)' : 'rgba(255,51,102,0.12)')
                : 'rgba(0,240,255,0.08)',
              border: `1px solid ${periodExpired ? (renewalMet ? 'var(--a, #39ff14)' : 'var(--d, #ff3366)') : 'var(--p, #00f0ff)'}`,
              borderRadius: '8px',
              marginTop: '12px'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                ⏱ 90-Day Renewal Period
              </div>
              {!periodExpired ? (
                <div style={{ fontSize: '.75rem' }}>
                  <div>Time remaining: <strong style={{ color: 'var(--p, #00f0ff)' }}>{formatDuration(secondsLeft)}</strong></div>
                  <div style={{ marginTop: '6px', color: 'var(--t2, #888)' }}>
                    Progress this period:
                  </div>
                  <div style={{ fontSize: '.7rem', marginTop: '2px' }}>
                    • Auto referrals: <strong>{eligibility.autoRefs} / {eligibility.minRefs}</strong>
                    {eligibility.autoRefs >= eligibility.minRefs ? ' ✓' : ''}
                  </div>
                  <div style={{ fontSize: '.7rem' }}>
                    • Volume: <strong>${formatNumber(eligibility.autoVolume)} / ${eligibility.minVolume}</strong>
                    {eligibility.autoVolume >= eligibility.minVolume ? ' ✓' : ''}
                  </div>
                </div>
              ) : renewalMet ? (
                <div style={{ fontSize: '.75rem', color: 'var(--a, #39ff14)' }}>
                  ✅ Renewal conditions met — timer will reset on next qualifying purchase by you or a direct referral
                </div>
              ) : (
                <div style={{ fontSize: '.75rem' }}>
                  <div style={{ color: 'var(--d, #ff3366)', fontWeight: 600, marginBottom: '6px' }}>
                    ⚠ Period expired — eligibility paused
                  </div>
                  <div style={{ fontSize: '.7rem', color: 'var(--t2, #888)' }}>
                    To resume earning:
                  </div>
                  <div style={{ fontSize: '.7rem' }}>
                    • Auto referrals: <strong>{eligibility.autoRefs} / {eligibility.minRefs}</strong>
                    {eligibility.autoRefs >= eligibility.minRefs ? ' ✓' : ' ✗'}
                  </div>
                  <div style={{ fontSize: '.7rem' }}>
                    • Volume: <strong>${formatNumber(eligibility.autoVolume)} / ${eligibility.minVolume}</strong>
                    {eligibility.autoVolume >= eligibility.minVolume ? ' ✓' : ' ✗'}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="eligibility-note" style={{ marginTop: '12px' }}>
            <span className="note-icon">💡</span>
            <span>Only business from direct referrals in Auto Mode counts towards these thresholds</span>
          </div>
        </div>
      </div>

      {/* Income Summary */}
      <div className="income-summary glass-card animate-fade-up delay-3">
        <div className="section-header">
          <h3>
            <span className="icon">💰</span>
            Income Summary
          </h3>
        </div>
        <div className="income-grid">
          <div className="income-box claimable">
            <span className="income-title">Claimable</span>
            <div className="income-details">
              <div className="income-row">
                <span>Sponsor</span>
                <span className="font-numbers">${formatSmart(parseFloat(claimableIncome.sponsor))}</span>
              </div>
              <div className="income-row">
                <span>Level</span>
                <span className="font-numbers">${formatSmart(parseFloat(claimableIncome.level))}</span>
              </div>
              <div className="income-row">
                <span>Auto-Ref</span>
                <span className="font-numbers">${formatSmart(parseFloat(claimableIncome.autoRef))}</span>
              </div>
              <div className="income-row total">
                <span>Total</span>
                <span className="font-numbers">${formatSmart(parseFloat(claimableIncome.total))}</span>
              </div>
            </div>
          </div>
          <div className="income-box missed">
            <span className="income-title">Missed</span>
            <div className="income-details">
              <div className="income-row">
                <span>Sponsor</span>
                <span className="font-numbers">${formatSmart(parseFloat(missedIncome.sponsor))}</span>
              </div>
              <div className="income-row">
                <span>Level</span>
                <span className="font-numbers">${formatSmart(parseFloat(missedIncome.level))}</span>
              </div>
              <div className="income-row total">
                <span>Total</span>
                <span className="font-numbers">${formatSmart(parseFloat(missedIncome.total))}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Level Stats */}
      <div className="level-stats glass-card animate-fade-up delay-4">
        <div className="section-header">
          <h3>
            <span className="icon">📊</span>
            Level Breakdown
          </h3>
        </div>
        <div className="level-grid">
          {levelStats.map((level, idx) => {
            // Determine if THIS level is unlocked based on the relevant threshold
            const isL16Plus = level.level >= 16;
            const unlocked = isL16Plus ? isL30Unlocked : isL15Unlocked;
            const activePeriod = !periodExpired || renewalMet;
            const fullyUnlocked = unlocked && activePeriod;
            return (
              <div
                key={`level-${level.level}`}
                className={`level-card ${parseInt(level.users) > 0 ? 'active' : ''}`}
                style={{ animationDelay: `${idx * 0.05}s` }}
              >
                <div className="level-header">
                  <span className="level-number">L{level.level}</span>
                  <span className={`level-status ${fullyUnlocked ? 'unlocked' : 'locked'}`}>
                    {fullyUnlocked ? '✓' : '🔒'}
                  </span>
                </div>
                <div className="level-data">
                  <div className="level-stat">
                    <span className="stat-value font-numbers">{level.users}</span>
                    <span className="stat-label">Users</span>
                  </div>
                  <div className="level-stat">
                    <span className="stat-value font-numbers">${formatNumber(parseFloat(level.business))}</span>
                    <span className="stat-label">Business</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info Note */}
      <div className="info-note animate-fade-up delay-5">
        <span className="note-icon">ℹ️</span>
        <p>
          <strong>L1–L15</strong> (0.025% each): needs <strong>${eligibility.reqL15}</strong> direct-auto business<br />
          <strong>L16–L{MATRIX_LEVELS}</strong> (0.01% each): needs <strong>${eligibility.reqL30}</strong> direct-auto business<br />
          Once eligible, renew every <strong>{Math.floor(eligibility.period / 86400)} days</strong> with at least
          <strong> {eligibility.minRefs}</strong> new auto-referral and <strong>${eligibility.minVolume}</strong> volume.
        </p>
      </div>
    </div>
  );
};

export default Team;
